import { Flex } from "styles/utility-components";

import { AiReport } from "components/reports/ai/AiReport";
import { Report } from "components/reports/Report";
import Button from "components/ui/button/Button";
import EmptyState from "components/ui/empty_state/EmptyState";
import CentredSpinner from "lib/core_components/CentredSpinner";
import { useReportConfig } from "lib/hooks/api/reports/useReportConfig";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import UTMParamsProvider from "lib/providers/UTMParams";
import { routes } from "lib/routes";
import { TrackingProvider } from "lib/tracking";

import css from "./ReportPage.module.scss";

type ReportPageProps = {
  reportId: string;
};

export function ReportPage({ reportId }: ReportPageProps) {
  const { data: report, isLoading, isError, error } = useReportConfig(reportId);
  const { data: currentUser } = useCurrentUser();

  if (isLoading) {
    return <CentredSpinner />;
  }

  if (isError || !report) {
    if (
      error instanceof Error &&
      error.message.includes(`Report config with id ${reportId} not found`)
    ) {
      return (
        <Flex flexGrow={1} alignItems="center" height="100%">
          <EmptyState
            type="loadError"
            heading="Report not found"
            description={`The report you are looking for does not exist or ${currentUser ? "you do not have permission to view it." : "is not publicly accessible."}`}
            width="100%"
            size="large"
            action={
              !currentUser && (
                <Button
                  as="a"
                  // eslint-disable-next-line no-restricted-syntax -- full page load to the auth route (not a SPA navigation)
                  href={`${routes.signIn}?redirect=${encodeURIComponent(window.location.pathname + window.location.search + window.location.hash)}`}
                >
                  Sign In
                </Button>
              )
            }
          />
        </Flex>
      );
    }

    return (
      <Flex flexGrow={1} alignItems="center" height="100%">
        <EmptyState
          type="loadError"
          heading="Sorry something has gone wrong loading this report"
          description="If this error persists please contact support"
          width="100%"
          size="large"
        />
      </Flex>
    );
  }

  return (
    <div className={css.reportPage}>
      <TrackingProvider
        data={{
          "Report ID": report.id,
          "Report title": report.reportData.title,
          CompanyId: report.companyId,
        }}
      >
        <UTMParamsProvider utmParams={{ source: "webapp", campaign: report.id, medium: "report" }}>
          {report.reportType !== "ai_report" ? (
            <Report report={report} />
          ) : (
            <AiReport report={report} />
          )}
        </UTMParamsProvider>
      </TrackingProvider>
    </div>
  );
}

export default ReportPage;
